import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { SignIn, ShieldCheck, Copy, CheckCircle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AuthenticatorSetupGuide from "../components/AuthenticatorSetupGuide";
import AdmissionStatus from "../components/AdmissionStatus";
import { useAdmissionPost, usePreloginAdmission, admissionCancelled } from "../lib/admission";

const MFA_TRUST_OPTIONS = [
  { hours: 0, label: "0 horas — pedir sempre" },
  { hours: 24, label: "24 horas" },
  { hours: 72, label: "72 horas" },
  { hours: 168, label: "1 semana" },
  { hours: 720, label: "1 mês" },
];

export default function LoginPage() {
  const admission = useAdmissionPost();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("login"); // login | mfa | setup | recovery
  const [challengeId, setChallengeId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [rememberHours, setRememberHours] = useState(0);
  const [setup, setSetup] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [pendingUser, setPendingUser] = useState(null);
  const { user, setUser } = useAuth();
  const prelogin = usePreloginAdmission(!user);
  const navigate = useNavigate();
  const location = useLocation();
  const requestedNext = new URLSearchParams(location.search).get("next") || "";
  const safeNext = requestedNext.startsWith("/live/") ? requestedNext : "/";

  if (user) return <Navigate to={safeNext} replace />;

  const finishLogin = (authenticatedUser) => {
    setUser(authenticatedUser);
    toast.success(`Bem-vindo, ${authenticatedUser.name}`);
    navigate(safeNext);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await admission.post("/auth/login", { email, password });
      const data = res.data || {};
      if (data.status === "authenticated") {
        finishLogin(data.user);
        return;
      }
      if (data.status === "mfa_required") {
        setChallengeId(data.challenge_id);
        setStage("mfa");
        return;
      }
      if (data.status === "mfa_setup_required") {
        setChallengeId(data.challenge_id);
        const setupRes = await api.post("/auth/mfa/setup/start", { challenge_id: data.challenge_id });
        setSetup(setupRes.data);
        setStage("setup");
        return;
      }
      throw new Error("Resposta de autenticação inesperada");
    } catch (err) {
      if (!admissionCancelled(err)) toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/mfa/verify", {
        challenge_id: challengeId,
        code: mfaCode,
        remember_hours: Number(rememberHours),
      });
      finishLogin(res.data.user);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/mfa/setup/confirm", {
        setup_id: setup.setup_id,
        code: mfaCode,
        remember_hours: Number(rememberHours),
      });
      setRecoveryCodes(res.data.recovery_codes || []);
      setPendingUser(res.data.user);
      setStage("recovery");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = async () => {
    await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Códigos copiados");
  };

  return (
    <div className="min-h-[calc(100vh-49px)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-16 bg-foreground text-background">
        <div>
          <div className="font-display text-3xl font-black tracking-tighter">
            SAKI<span className="text-primary">/</span>X<span className="text-[0.7em]">m</span>
          </div>
          <div className="label-eyebrow mt-2 text-background/60">sistema de provas e controle acadêmico</div>
        </div>
        <div>
          <h1 className="font-display text-5xl font-black tracking-tighter leading-[1.05]">
            Corrija provas<br /><span className="text-primary">em segundos.</span>
          </h1>
          <p className="mt-6 text-background/60 text-sm max-w-md">
            Sessões revogáveis, autenticação em duas etapas e proteção contra tentativas automatizadas.
          </p>
        </div>
        <div className="label-eyebrow text-background/40">● segurança reforçada · sessão protegida · auditoria</div>
      </div>

      <div className="flex items-center justify-center p-8">
        {stage === "login" && !prelogin.ready && (
          <div className="w-full max-w-sm space-y-6" data-testid="prelogin-waiting-room">
            <div>
              <div className="label-eyebrow">antessala segura</div>
              <h2 className="font-display text-4xl font-black tracking-tighter mt-2">Aguarde sua vez.</h2>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                O acesso ao formulário é liberado em ordem de chegada, até 30 pessoas por segundo. Nenhum e-mail ou senha foi solicitado ou enviado.
              </p>
            </div>
            {prelogin.queue ? (
              <AdmissionStatus queue={prelogin.queue} onCancel={prelogin.cancel} />
            ) : prelogin.error ? (
              <div className="border border-destructive bg-destructive/5 p-5 space-y-4 text-sm">
                <p>{prelogin.error}</p>
                <Button type="button" variant="outline" className="w-full rounded-none" onClick={prelogin.retry}>Entrar novamente na fila</Button>
              </div>
            ) : (
              <div className="border border-border bg-muted/20 p-5 flex items-center gap-3 text-sm text-muted-foreground" role="status">
                <Spinner size={20} className="animate-spin text-primary" /> Organizando a entrada…
              </div>
            )}
            <div className="border-t border-border pt-5 text-center">
              <Link to="/apresentar" className="text-sm text-primary font-semibold hover:underline">
                Projetar uma prova ao vivo sem fazer login
              </Link>
            </div>
          </div>
        )}

        {stage === "login" && prelogin.ready && (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
            <div>
              <div className="label-eyebrow">acesse sua conta</div>
              <h2 className="font-display text-4xl font-black tracking-tighter mt-2">Entrar.</h2>
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="label-eyebrow">e-mail</span>
                <Input data-testid="login-email" type="email" autoComplete="email" required disabled={loading} value={email}
                  onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-none font-mono-data" />
              </label>
              <label className="block">
                <span className="label-eyebrow">senha</span>
                <Input data-testid="login-password" type="password" autoComplete="current-password" required disabled={loading} value={password}
                  onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-none font-mono-data" />
              </label>
              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">Esqueci minha senha</Link>
              </div>
            </div>
            <AdmissionStatus queue={admission.queue} onCancel={admission.cancel} />
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full rounded-none font-mono-data uppercase tracking-widest text-xs">
              <SignIn size={14} weight="bold" className="mr-2" />
              {admission.queue ? "Aguardando sua vez…" : loading ? "Verificando…" : "Entrar"}
            </Button>
            <div className="text-sm text-muted-foreground text-center">
              Recebeu um convite?{" "}
              <Link to="/register" data-testid="link-register" className="text-primary font-semibold hover:underline">
                Ativar cadastro
              </Link>
            </div>
            <div className="border-t border-border pt-5 text-center">
              <Link to="/apresentar" className="text-sm text-primary font-semibold hover:underline" data-testid="open-live-display">
                Projetar uma prova ao vivo sem fazer login
              </Link>
            </div>
          </form>
        )}

        {stage === "mfa" && (
          <form onSubmit={submitMfa} className="w-full max-w-sm space-y-6">
            <div>
              <ShieldCheck size={34} weight="duotone" className="text-primary" />
              <div className="label-eyebrow mt-4">segunda etapa</div>
              <h2 className="font-display text-4xl font-black tracking-tighter mt-2">Confirme.</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Abra o Microsoft Authenticator, selecione <strong>SAKI/Xm</strong> (ou <strong>SAKI X TESTS</strong> em contas antigas) e digite o código de 6 dígitos. Também é possível usar um código de recuperação.
              </p>
              <a
                href="https://aka.ms/mobileauthenticator"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center text-xs text-primary hover:underline mt-2"
              >
                Baixar ou abrir o Microsoft Authenticator
              </a>
            </div>
            <Input autoFocus autoComplete="one-time-code" value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ""))}
              className="rounded-none font-mono-data text-center tracking-[0.25em] text-xl" />
            <label className="block border border-border p-3">
              <span className="label-eyebrow">Confiar neste navegador</span>
              <select
                value={rememberHours}
                onChange={(e) => setRememberHours(Number(e.target.value))}
                className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm font-mono-data"
                data-testid="mfa-remember-hours"
              >
                {MFA_TRUST_OPTIONS.map((item) => (
                  <option key={item.hours} value={item.hours}>{item.label}</option>
                ))}
              </select>
              <span className="mt-2 block text-xs text-muted-foreground">
                A senha continua obrigatória. Somente o código do Authenticator fica dispensado neste navegador.
              </span>
            </label>
            <Button type="submit" disabled={loading || mfaCode.length < 6}
              className="w-full rounded-none font-mono-data uppercase tracking-widest text-xs">
              {loading ? "Verificando…" : "Confirmar acesso"}
            </Button>
            <button type="button" onClick={() => { setStage("login"); setMfaCode(""); }}
              className="w-full text-sm text-muted-foreground hover:text-foreground">Voltar</button>
          </form>
        )}

        {stage === "setup" && setup && (
          <form onSubmit={confirmSetup} className="w-full max-w-3xl space-y-5">
            <div>
              <ShieldCheck size={34} weight="duotone" className="text-primary" />
              <div className="label-eyebrow mt-4">proteção obrigatória</div>
              <h2 className="font-display text-3xl font-black tracking-tighter mt-2">Ative a verificação em duas etapas.</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Administradores e gestores precisam usar um aplicativo autenticador.
              </p>
            </div>
            <AuthenticatorSetupGuide
              setupQrDataUrl={setup.qr_data_url}
              manualKey={setup.manual_key}
            />
            <Input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
              placeholder="Código de 6 dígitos"
              className="rounded-none font-mono-data text-center tracking-[0.35em] text-xl" />
            <label className="block border border-border p-3">
              <span className="label-eyebrow">Confiar neste navegador</span>
              <select value={rememberHours} onChange={(e) => setRememberHours(Number(e.target.value))}
                className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm font-mono-data">
                {MFA_TRUST_OPTIONS.map((item) => (
                  <option key={item.hours} value={item.hours}>{item.label}</option>
                ))}
              </select>
            </label>
            <Button type="submit" disabled={loading || mfaCode.length !== 6}
              className="w-full rounded-none font-mono-data uppercase tracking-widest text-xs">
              {loading ? "Ativando…" : "Ativar e entrar"}
            </Button>
          </form>
        )}

        {stage === "recovery" && (
          <div className="w-full max-w-md space-y-5">
            <div>
              <CheckCircle size={34} weight="fill" className="text-primary" />
              <div className="label-eyebrow mt-4">última etapa</div>
              <h2 className="font-display text-3xl font-black tracking-tighter mt-2">Guarde os códigos de recuperação.</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Cada código funciona uma única vez. Guarde-os fora deste computador, preferencialmente em um gerenciador de senhas.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 border border-border bg-card p-4">
              {recoveryCodes.map((code) => <code key={code} className="font-mono-data text-sm text-center py-1">{code}</code>)}
            </div>
            <Button variant="outline" onClick={copyRecoveryCodes} className="w-full rounded-none">
              <Copy size={14} className="mr-2" /> Copiar códigos
            </Button>
            <Button onClick={() => finishLogin(pendingUser)} className="w-full rounded-none">
              Já guardei os códigos
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
